// ⌘K command palette (U11): jump to any visible page, open an "add" form, or
// find a customer / supplier by name (opens its detail drawer).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Dialog, InputBase, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Typography } from '@mui/material';
import { Add, Profile2User, SearchNormal1, Truck } from 'iconsax-reactjs';

import { adminApi } from '../../../api/client';
import { canAny } from '../../../auth/permissions';
import { t } from '../../../i18n';
import type { ShellUser } from '../api';
import type { NavItem } from '../nav';
import { SHORTCUTS, comboText, shortcutLabel } from '../shortcuts';

type Entry = { id: string; label: string; hint?: string; group: string; icon: NavItem['icon']; run: () => void };

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

export function CommandPalette({
  open,
  onClose,
  navItems,
  user
}: {
  open: boolean;
  onClose: () => void;
  navItems: NavItem[];
  user: ShellUser | null;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState<Entry[]>([]);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setRemote([]);
    }
  }, [open]);

  const go = (to: string, newTab?: boolean) => {
    onClose();
    if (newTab) window.open(new URL(to.replace(/^\//, ''), document.baseURI).toString(), '_blank', 'noopener');
    else navigate(to.startsWith('/') ? to : `/${to}`);
  };

  const staticEntries = useMemo<Entry[]>(() => {
    const pages: Entry[] = [];
    const seen = new Set<string>();
    for (const item of navItems) {
      if (seen.has(item.to)) continue;
      seen.add(item.to);
      pages.push({ id: `page:${item.to}`, label: item.label, group: t('shell.palette.pages'), icon: item.icon, run: () => go(item.to === '.' ? '/' : item.to, item.newTab) });
    }
    const visibleRoutes = new Set(navItems.map((i) => i.to));
    const creates: Entry[] = SHORTCUTS.filter((s) => s.create && s.to && visibleRoutes.has(s.to.split('?')[0])).map((s) => ({
      id: `create:${s.to}`,
      label: shortcutLabel(s),
      hint: comboText(s),
      group: t('shell.palette.actions'),
      icon: Add,
      run: () => go(s.to!)
    }));
    return [...pages, ...creates];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navItems]);

  // Live party search (debounced).
  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setRemote([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      const sources = [
        { endpoint: 'customers/search', page: 'customers', permission: 'read-customers', icon: Profile2User, group: t('nav.customers') },
        { endpoint: 'suppliers/search', page: 'suppliers', permission: 'read-suppliers', icon: Truck, group: t('nav.suppliers') }
      ].filter((s) => canAny(user, [s.permission]));
      const results = await Promise.all(
        sources.map((source) =>
          adminApi
            .get(source.endpoint, { params: { q } })
            .then((r) =>
              (Array.isArray(r.data) ? r.data : []).slice(0, 5).map((row: { value: string; label: string }) => ({
                id: `${source.page}:${row.value}`,
                label: String(row.label),
                group: source.group,
                icon: source.icon,
                run: () => go(`${source.page}?view=${row.value}`)
              }))
            )
            .catch(() => [] as Entry[])
        )
      );
      setRemote(results.flat());
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, user]);

  const entries = useMemo(() => {
    const q = normalize(query.trim());
    const local = q ? staticEntries.filter((e) => normalize(e.label).includes(q)) : staticEntries.filter((e) => e.id.startsWith('page:'));
    return [...local.slice(0, 30), ...remote];
  }, [query, remote, staticEntries]);

  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(entries.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      entries[active]?.run();
    }
  };

  let lastGroup = '';
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { alignSelf: 'flex-start', mt: 10 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <SearchNormal1 size={18} />
        <InputBase
          autoFocus
          fullWidth
          placeholder={t('shell.palette.placeholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          inputProps={{ 'aria-label': t('shell.commandPalette'), 'data-testid': 'command-input' }}
        />
        <Chip size="small" label="Esc" variant="outlined" />
      </Box>
      <List ref={listRef} dense sx={{ maxHeight: 420, overflowY: 'auto', py: 0 }}>
        {entries.length ? (
          entries.map((entry, index) => {
            const header = entry.group !== lastGroup ? entry.group : null;
            lastGroup = entry.group;
            const Icon = entry.icon;
            return (
              <Box key={entry.id}>
                {header ? <ListSubheader sx={{ lineHeight: '32px', fontWeight: 700 }}>{header}</ListSubheader> : null}
                <ListItemButton data-index={index} selected={index === active} onMouseEnter={() => setActive(index)} onClick={entry.run}>
                  <ListItemIcon sx={{ minWidth: 34 }}>
                    <Icon size={18} />
                  </ListItemIcon>
                  <ListItemText primary={entry.label} />
                  {entry.hint ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {entry.hint}
                    </Typography>
                  ) : null}
                </ListItemButton>
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t('shell.palette.empty')}
          </Typography>
        )}
      </List>
    </Dialog>
  );
}
